import React, { useEffect } from 'react';
import { Trash2, Sparkles, Lock, Package, Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/hpp/SearchableSelect';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);

const hitungJasa = (ml) => {
  if (!ml || ml === 0) return 0;
  if (ml <= 10) return 4000;
  if (ml <= 30) return 10000;
  if (ml <= 50) return 15000;
  return 20000;
};

const safeFloat = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(String(val).replace(/,/g, '.'));
  return isNaN(parsed) ? 0 : parsed;
};

export default function ClientProductCard({ 
  product, 
  index, 
  botolList, 
  tutupList, 
  sprayList, 
  bahanList, 
  itemTambahanList = [], 
  isLocked = true, 
  varianDatabase = [], 
  onChange, 
  onRemove, 
  canRemove 
}) {
  const botolDariClient = product.botol_dari_client === true;
  const tutupDariClient = product.tutup_dari_client === true;
  const sprayDariClient = product.spray_dari_client === true;

  const botol = botolDariClient ? null : botolList.find(b => b.id === product.botol_id);
  const tutup = tutupDariClient ? null : tutupList.find(t => t.id === product.tutup_id);
  const spray = sprayDariClient ? null : sprayList.find(s => s.id === product.spray_id);

  const ukuranLabelMl = botolDariClient ? (product.botol_ukuran_client || 0) : (botol?.ukuran_label_ml ?? product.selected_size ?? 0);
  
  // Lookup variant & size object in database
  const varianObj = varianDatabase.find(v => String(v.id).toLowerCase() === String(product.variant_id).toLowerCase());
  const selectedSizeObj = varianObj?.sizes?.find(s => String(s.size) === String(product.selected_size)) || varianObj?.sizes?.[0];
  const dbBahan = selectedSizeObj?.bahan;
  
  const customHargaJual = product.custom_harga_jual || selectedSizeObj?.custom_harga_jual || dbBahan?.custom_harga_jual;
  const customMargin = product.custom_margin || selectedSizeObj?.custom_margin || dbBahan?.custom_margin;
  const customJasa = product.custom_jasa || selectedSizeObj?.custom_jasa || dbBahan?.custom_jasa;

  const otomatisJasa = customJasa !== undefined && customJasa !== null ? customJasa : hitungJasa(ukuranLabelMl);

  // Fallback calculation if no direct custom_harga_jual is stored
  const jualBotol = botolDariClient ? 0 : getHargaJual(botol);
  const jualTutup = tutupDariClient ? 0 : getHargaJual(tutup);
  const jualSpray = sprayDariClient ? 0 : getHargaJual(spray);
  const jualPackaging = jualBotol + jualTutup + jualSpray;

  const calcJualRacikan = (ukuran) => {
    return (product.racikan || []).reduce((sum, r) => {
      if (!r.bahan_id || !r.persentase) return sum;
      const vol = (safeFloat(r.persentase) / 100) * ukuran;
      const isLiter = r.satuan === 'g'; 
      const hargaJualPerSatuan = r.harga_jual_satuan || (r.harga_per_satuan * 1.3) || 0;

      if (isLiter) {
        const hargaPerMl = hargaJualPerSatuan / 1000;
        return sum + vol * hargaPerMl;
      }
      const satuan = r.satuan || 'kg';
      const perMl = (satuan === 'kg' || satuan === 'liter') ? hargaJualPerSatuan / 1000 : hargaJualPerSatuan;
      return sum + perMl * vol;
    }, 0);
  };

  const jualRacikan = calcJualRacikan(ukuranLabelMl);
  const jualKomponen = (product.komponen_tambahan || []).reduce((sum, k) => sum + (k.harga_jual || 0), 0);
  const calculatedJualTotal = Math.round(jualPackaging + jualRacikan + otomatisJasa + jualKomponen);
  
  // Final unit price per pcs (Harga Satuan)
  let unitPrice = 0;
  if (customHargaJual !== undefined && customHargaJual !== null && customHargaJual !== '' && Number(customHargaJual) > 0) {
    unitPrice = Number(customHargaJual);
  } else if (customMargin !== undefined && customMargin !== null && customMargin !== '') {
    unitPrice = calculatedJualTotal + Number(customMargin);
  } else if (calculatedJualTotal > 0) {
    unitPrice = calculatedJualTotal;
  } else if (Number(product.harga_jual_per_pcs) > 0) {
    unitPrice = Number(product.harga_jual_per_pcs);
  }

  const currentQty = Math.max(0, parseInt(product.qty) || 0);
  const currentSubtotal = unitPrice * currentQty;

  // Sync unit price and subtotal into product state
  useEffect(() => {
    if (unitPrice > 0 && (product.harga_jual_per_pcs !== unitPrice || product.total_harga_jual !== currentSubtotal)) {
      onChange({
        jasa_nominal: otomatisJasa,
        harga_jual_per_pcs: unitPrice,
        total_harga_jual: currentSubtotal,
      });
    }
  }, [unitPrice, product.qty, product.harga_jual_per_pcs, currentSubtotal, otomatisJasa]);

  const handleVariantSelect = (val) => {
    const varian = varianDatabase.find(v => v.id === val);
    if (varian) {
      const defaultSize = varian.sizes && varian.sizes.length > 0 ? varian.sizes[0] : null;
      const initialHarga = Number(defaultSize?.custom_harga_jual) || 0;
      const initialQty = parseInt(product.qty) || 0;
      onChange({
        variant_id: varian.id,
        nama_product: varian.nama_product,
        selected_size: defaultSize?.size || 0,
        custom_harga_jual: initialHarga,
        harga_jual_per_pcs: initialHarga,
        total_harga_jual: initialHarga * initialQty,
        botol_id: defaultSize?.botol_id || null,
        tutup_id: defaultSize?.tutup_id || null,
        spray_id: defaultSize?.spray_id || null,
        botol_dari_client: defaultSize ? defaultSize.botol_dari_client : true,
        botol_ukuran_client: defaultSize?.botol_ukuran_client || parseFloat(defaultSize?.size) || 0,
        racikan: defaultSize?.racikan || [],
        komponen_tambahan: defaultSize?.komponen_tambahan || []
      });
    }
  };

  const handleSizeSelect = (s) => {
    const initialHarga = Number(s?.custom_harga_jual) || Number(product.harga_jual_per_pcs) || 0;
    const initialQty = parseInt(product.qty) || 0;
    onChange({
      selected_size: s.size,
      custom_harga_jual: initialHarga,
      harga_jual_per_pcs: initialHarga,
      total_harga_jual: initialHarga * initialQty,
      botol_id: s.botol_id,
      tutup_id: s.tutup_id,
      spray_id: s.spray_id,
      botol_dari_client: s.botol_dari_client,
      botol_ukuran_client: s.botol_ukuran_client || parseFloat(s.size) || 0,
      racikan: s.racikan || [],
      komponen_tambahan: s.komponen_tambahan || []
    });
  };

  const handleQtyInput = (val) => {
    if (val === '' || val === null || val === undefined) {
      onChange({ qty: '', total_harga_jual: 0 });
      return;
    }
    const parsed = Math.max(0, parseInt(val) || 0);
    onChange({ 
      qty: parsed,
      total_harga_jual: unitPrice * parsed
    });
  };

  return (
    <div className="bg-white border border-[#E6D5B8] rounded-2xl shadow-xs overflow-hidden transition-all">
      {/* Top Header Row with Circle Number and Variant Selection */}
      <div className="p-4 md:p-5 bg-gradient-to-r from-[#F9F5EC]/80 via-white to-[#F9F5EC]/40 border-b border-[#E6D5B8]/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-full bg-[#8C6208] text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-xs">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            {varianDatabase.length > 0 ? (
              <SearchableSelect
                options={varianDatabase.map(v => ({ value: v.id, label: v.nama_product }))}
                value={product.variant_id}
                onChange={handleVariantSelect}
                placeholder="Contoh: Sun Zest Serum..."
                className="w-full h-10 text-sm font-semibold rounded-xl border-[#E6D5B8] bg-white focus:ring-[#8C6208]"
              />
            ) : (
              <Input
                value={product.nama_product}
                onChange={(e) => onChange({ nama_product: e.target.value })}
                placeholder="Contoh: Sun Zest Serum..."
                className="h-10 rounded-xl border-[#E6D5B8] bg-white font-medium text-sm"
              />
            )}
          </div>
        </div>

        {canRemove && (
          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
            <button
              type="button"
              onClick={onRemove}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition-colors cursor-pointer"
              title="Hapus varian ini"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Card Body */}
      <div className="p-4 md:p-6 space-y-4">
        {/* Row 1: Kuantitas Pesanan & Ukuran Botol */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1. Kuantitas Pesanan (Pcs) */}
          <div>
            <label className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider mb-1.5 block">
              Kuantitas Pesanan (Pcs)
            </label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={product.qty === '' ? '' : (product.qty ?? 0)}
              onChange={(e) => handleQtyInput(e.target.value)}
              className="h-10 font-bold text-[#302621] border-[#E6D5B8] rounded-xl text-sm bg-white focus-visible:ring-1 focus-visible:ring-[#8C6208] w-full px-3.5"
            />
          </div>

          {/* 2. Ukuran Botol (ml) */}
          <div>
            <label className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider mb-1.5 block">
              Ukuran Botol (ml)
            </label>
            {varianObj?.sizes?.length > 1 ? (
              <div className="flex flex-wrap gap-2">
                {varianObj.sizes.map((s) => {
                  const isSelected = String(product.selected_size) === String(s.size);
                  return (
                    <button
                      key={s.size}
                      type="button"
                      onClick={() => handleSizeSelect(s)}
                      className={`h-10 px-4 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-[#8C6208] text-white shadow-sm ring-2 ring-[#8C6208]/30'
                          : 'bg-[#F9F5EC] text-[#302621] border border-[#E6D5B8] hover:bg-[#E6D5B8]/40'
                      }`}
                    >
                      {s.size} ml
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                <span className="text-sm font-semibold text-[#302621]">
                  {product.selected_size > 0 ? `${product.selected_size} ml` : (product.nama_product ? 'Ukuran Standar' : 'Pilih varian dulu')}
                </span>
                <span className="text-xs text-slate-400 font-medium">ml</span>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Harga per Varian & Total Harga */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          {/* Harga per Varian */}
          <div className="bg-[#FFFDF9] border border-[#E6D5B8] rounded-xl p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider mb-1 block">
              Harga per Varian:
            </span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="font-black text-base text-slate-800">
                Rp {fmt(unitPrice)}
              </span>
              <span className="text-[11px] text-slate-500 font-bold">/ pcs</span>
            </div>
          </div>

          {/* Total Harga */}
          <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-xl p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-[#8C6208] uppercase tracking-wider mb-1 block">
              Total Harga:
            </span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-xs font-bold text-[#7A695B]">
                {currentQty} pcs × Rp {fmt(unitPrice)}
              </span>
              <span className="font-black text-lg text-[#8C6208]">
                Rp {fmt(currentSubtotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
